/**
 * Drag + resize helpers for the SHSAT Line Reader mask overlay.
 *
 * Extracted verbatim from the legacy `Module/index.tsx` component-local
 * implementations so the adaptive module (`AdaptiveModule`) can render the
 * same line-reader object. Pure DOM utilities — no React state/refs — so they
 * are safe to share. The legacy component still defines its own private copies;
 * this file is consumed only by the adaptive module to avoid touching legacy.
 *
 * Both operate on elements located by id within `#digitalSHSATModule__body`
 * and rely on the `digitalSHSATModule__*` CSS in `Module/index.css`.
 */

/** Make `body` drag `element` within `parent`, optionally scaling `content`. */
export const dragMove = (
    element: HTMLElement,
    body: HTMLElement,
    parent: HTMLElement,
    content?: HTMLElement,
    padding: number = 0,
    allowOverflow: boolean = false,
) => {
    let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;
    // Pointer events cover mouse + touch (iPad) in one path; the draggable
    // elements set touch-action:none (DigitalSHSAT/index.css) so touch-dragging
    // controls the gesture instead of scrolling the page.
    const dragMouseDown = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
        element.style.transform = "none";
        pos3 = event.clientX;
        pos4 = event.clientY;
        document.onpointermove = dragElement;
        document.onpointerup = closeDragElement;
    };

    const dragElement = (event: any) => {
        event.preventDefault();
        event.stopPropagation();

        pos1 = pos3 - event.clientX;
        pos2 = pos4 - event.clientY;
        pos3 = event.clientX;
        pos4 = event.clientY;

        const parentRect = parent.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        let newTop = element.offsetTop - pos2;
        let newLeft = element.offsetLeft - pos1;

        if (!allowOverflow) {
            newTop = Math.max(padding, Math.min(newTop, parentRect.height - elementRect.height - padding));
            newLeft = Math.max(padding, Math.min(newLeft, parentRect.width - elementRect.width - padding));
        }

        element.style.top = `${newTop}px`;
        element.style.left = `${newLeft}px`;

        if (content) {
            const translateLeft = newLeft * (2 * parent.offsetWidth - element.offsetWidth) / (parent.offsetWidth - element.offsetWidth);
            const translateTop = newTop * (2 * parent.offsetHeight - element.offsetHeight) / (parent.offsetHeight - element.offsetHeight);
            content.style.transform = `translate(-${translateLeft}px, -${translateTop}px) scale(2)`;
        }
    };

    const closeDragElement = () => {
        document.onpointerup = null;
        document.onpointermove = null;
    };

    body.onpointerdown = (event: any) => {
        const isInsideContent = event.target instanceof Element &&
            (event.target.closest('#digitalSHSATModule__lineReaderResize') || event.target.closest('#digitalSHSATModule__lineReaderResize'));
        if (isInsideContent) return;
        dragMouseDown(event);
    };
};

/** Make `icon` resize `element` within `parent`, keeping `child` inside. */
export const popupResize = (
    element: HTMLElement,
    icon: HTMLElement,
    parent: HTMLElement,
    child?: HTMLElement,
) => {
    let startX = 0, startY = 0, startWidth = 0, startHeight = 0;
    let startElementLeft = 0, startElementTop = 0;

    const dragMouseDown = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
        startWidth = element.offsetWidth;
        startHeight = element.offsetHeight;
        startElementLeft = element.offsetLeft;
        startElementTop = element.offsetTop;

        startX = event.clientX;
        startY = event.clientY;
        document.onpointermove = dragElement;
        document.onpointerup = closeDragElement;
    };

    const dragElement = (event: any) => {
        event.preventDefault();
        event.stopPropagation();

        const moveX: number = event.clientX - startX + 20;
        const moveY: number = event.clientY - startY + 20;

        const parentRect = parent.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        const maxWidth = parentRect.width - (elementRect.left - parentRect.left);
        const maxHeight = parentRect.height - (elementRect.top - parentRect.top);

        const minChildWidth = child ? child.offsetWidth : 120;
        const minChildHeight = child ? child.offsetHeight : 70;

        let newWidth = Math.max(child ? minChildWidth + 60 : minChildWidth, Math.min(startWidth + moveX, maxWidth));
        let newHeight = Math.max(child ? minChildHeight + 70 : minChildHeight, Math.min(startHeight + moveY, maxHeight));

        element.style.width = `${newWidth - 20}px`;
        element.style.height = `${newHeight - 20}px`;

        const element2 = document.getElementById(`${element.id}2`);
        if (element2) {
            element2.style.width = `${newWidth - 20}px`;
            element2.style.height = `${newHeight - 20}px`;
        }

        const paddingX = 20;
        const paddingY = 20;
        if (child) {
            const childWidth = child.offsetWidth;
            const childHeight = child.offsetHeight;
            const elementWidth = element.offsetWidth;
            const elementHeight = element.offsetHeight;

            const childLeft = parseFloat(child.style.left || "0");
            const childTop = parseFloat(child.style.top || "0");

            const rightEdge = childLeft + childWidth;
            const bottomEdge = childTop + childHeight;

            const insideX = rightEdge >= elementWidth - paddingX;
            const insideY = bottomEdge >= elementHeight - paddingY;

            if (insideX || insideY) {
                if (insideX) {
                    child.style.left = `${elementWidth - childWidth - paddingX}px`;
                }
                if (insideY) {
                    child.style.top = `${elementHeight - childHeight - paddingY}px`;
                }

                const availableWidth = elementWidth - paddingX * 2;
                const availableHeight = elementHeight - paddingY * 2;

                const newChildWidth = Math.max(minChildWidth, Math.min(childWidth, availableWidth));
                const newChildHeight = Math.max(minChildHeight, Math.min(childHeight, availableHeight));

                child.style.width = `${newChildWidth}px`;
                child.style.height = `${newChildHeight}px`;

                if (insideX) {
                    child.style.left = `${elementWidth - newChildWidth - paddingX}px`;
                }
                if (insideY) {
                    child.style.top = `${elementHeight - newChildHeight - paddingY}px`;
                }
            }
        }
    };

    const closeDragElement = () => {
        document.onpointerup = null;
        document.onpointermove = null;
    };

    icon.onpointerdown = dragMouseDown;
};
